import type {Sorting} from "@/types/Sorting";
import  {type LiteClient,liteClient as algoliasearch} from "algoliasearch/lite";

import { createContext, type FC, type PropsWithChildren, useContext, useState } from "react";
import { parseJson } from "@/utils/parseJson.ts";

interface AlgoliaShopConfig {
	algoliaConfig: Omit<AlgoliaConfig, "tc">;
	tc: Translations;
	algoliaRefinements: AlgoliaRefinements;
	searchClient: LiteClient;
	sorting: Sorting[];
	formater?: GlobalFormat;
	recommendationIds?: string[];
	recommendationType?: string|"trending"|"related";
	recommendationTitle?: string;
	recommendationItemNo?: string;
}

const AlgoliaShopConfigContext = createContext<AlgoliaShopConfig | undefined>(undefined);

interface AlgoliaShopConfigProviderProps {
	domElement: HTMLElement;
}

export const AlgoliaShopConfigProvider: FC<PropsWithChildren<AlgoliaShopConfigProviderProps>> = ({ children, domElement }) => {
	const configFromElement: AlgoliaConfig = parseJson(domElement.dataset.config);
	if (!configFromElement) throw new Error("missing configuration");
	const { tc: translations, sorting: shopSorting = [] } = configFromElement;
	const [tc] = useState(translations);
	const [algoliaConfig] = useState<AlgoliaShopConfig["algoliaConfig"]>(configFromElement);
	const [formater] = useState(window?.GLOBALS?.format);
	const [algoliaRefinements] = useState(parseJson(domElement.dataset.refinements));
	const [recommendationType] = useState(domElement.dataset.recommendationType);
	const [recommendationTitle] = useState(domElement.dataset.recommendationTitle);
	const [recommendationIds] = useState(parseJson(domElement.dataset.recommendationIds));
	const [recommendationItemNo] = useState(domElement.dataset.recommendationItemNo);
	const [searchClient] = useState(algoliasearch(algoliaConfig.algoliaAppId, algoliaConfig.algoliaSearchKey));
	const [sorting] = useState(
		shopSorting.map(({ label, index, code }) => {
			return {
				label: label,
				value: index,
				code: code,
			};
		})
	);
	return (
		<AlgoliaShopConfigContext.Provider
			value={{
				algoliaConfig,
				algoliaRefinements,
				searchClient,
				tc,
				sorting,
				recommendationIds,
				formater,
				recommendationType,
				recommendationItemNo,
				recommendationTitle
			}}
		>
			{children}
		</AlgoliaShopConfigContext.Provider>
	);
};
export const useAlgoliaShopConfig = () => {
	const context = useContext(AlgoliaShopConfigContext);
	if (!context) throw new Error("useAlgoliaShopConfigContext must be used within a ConfigProvider");
	return context;
};
